import { httpClient } from './httpClient';
import { ApiResponse } from './errors';
import { formatDateGSheet } from '@/utils.ts/date';
import type { AnalyticsEventPayload } from '@/analytics/client';

/**
 * Strip persist-only UI state before sending booking data over the wire.
 *
 * `bookingData.getData()` includes `venuesByDate` (a date→venues availability
 * map) so it can be restored after a page refresh, and `appliedCouponCodes`
 * (the customer's entered coupon codes) so they survive a reload. Neither is
 * part of the booking contract — the backend DTOs (checkout-session,
 * menu-selection) run under `forbidNonWhitelisted: true` and reject unknown
 * fields with a 400 Bad Request. Coupon codes travel top-level as
 * `couponCodes` on `startPayment`, never inside `data`.
 * Returns a shallow copy so the caller's object (reused for persist()) is
 * left untouched.
 */
function toWirePayload<T extends Record<string, unknown>>(
  bookingData: T,
): Omit<T, 'venuesByDate' | 'appliedCouponCodes'> {
  if (bookingData && typeof bookingData === 'object') {
    const { venuesByDate: _omitVenues, appliedCouponCodes: _omitCoupons, ...rest } = bookingData as Record<string, unknown>;
    void _omitVenues;
    void _omitCoupons;
    return rest as Omit<T, 'venuesByDate' | 'appliedCouponCodes'>;
  }
  return bookingData;
}

// Read-endpoint cutover — these now hit the NestJS routes (mounted under /api/
// by nginx in prod and proxied by Vite locally — see vite.config.mts:server.proxy).
// Writes (startPayment, checkVatNumber) still go to the legacy PHP endpoints
// until those are ported too.

/**
 * Get booking availability for a specific month
 * @param year - The year to check availability
 * @param month - The month to check availability (1-12)
 * @param venue - Optional venue to filter availability by dining venue
 * @returns Promise with availability dates
 */
async function getAvailability(year: number, month: number, venue?: string): Promise<ApiResponse<any[]>> {
  return httpClient.get<any[]>('api/availability', {
    params: { year, month, ...(venue && { venue }) },
  });
}

/**
 * Get available services for a specific date
 * @param date - The date to get services for
 */
async function getServices(date: Date): Promise<ApiResponse<any[]>> {
  return httpClient.get<any[]>('api/services', {
    params: { date: formatDateGSheet(date) },
  });
}

/**
 * Get available food options
 */
async function getFoods(): Promise<ApiResponse<any[]>> {
  return httpClient.get<any[]>('api/foods');
}

/**
 * Get menu for a specific date
 * @param date - The date to get the menu for
 */
async function getMenu(date: Date): Promise<ApiResponse<any[]>> {
  return httpClient.get<any[]>('api/menu', {
    params: { date: formatDateGSheet(date) },
  });
}

/**
 * Get available drinks
 */
async function getDrinks(): Promise<ApiResponse<any[]>> {
  return httpClient.get<any[]>('api/drinks');
}

/**
 * Get available options
 */
async function getOptions(): Promise<ApiResponse<any[]>> {
  return httpClient.get<any[]>('api/options');
}

/**
 * Get text content/labels
 */
async function getTexts(): Promise<ApiResponse<any[]>> {
  return httpClient.get<any[]>('api/texts');
}

/**
 * Result of `POST /api/checkout-session`. A normal booking returns
 * `checkout_url` for the Stripe redirect. A coupon that fully covers the
 * total (remainingDueCents === 0) skips Stripe entirely and returns
 * `confirmed: true` + `booking_id` instead.
 */
export interface StartPaymentResponse {
  checkout_url?: string;
  confirmed?: boolean;
  booking_id?: string;
}

/**
 * Initiate Stripe payment process
 * @param date - Booking date
 * @param type - Booking type
 * @param client - Client information
 * @param amount - Payment amount
 * @param bookingData - Complete booking data
 * @param couponCodes - Coupon codes applied by the customer, if any
 */
async function startPayment(
  date: string,
  type: string,
  client: string,
  amount: number,
  bookingData: any,
  noonInviteToken?: string | null,
  acknowledgeNoon?: boolean,
  couponCodes?: string[],
): Promise<ApiResponse<StartPaymentResponse>> {
  console.log('Starting payment', { date, type, client, amount, data: bookingData });

  return httpClient.post<StartPaymentResponse>('api/checkout-session', {
    date,
    type,
    client,
    amount,
    data: toWirePayload(bookingData),
    ...(noonInviteToken ? { noonInviteToken } : {}),
    ...(acknowledgeNoon ? { acknowledgeNoon: true } : {}),
    ...(couponCodes && couponCodes.length > 0 ? { couponCodes } : {}),
  });
}

/**
 * Validate a noon invite token. Never throws — resolves to {success: false,
 * status: 404|410} for unknown/expired invites (HttpClient converts all
 * errors to ApiResponse). Callers must check `result.success`.
 */
async function validateNoonInvite(token: string): Promise<ApiResponse<{ date: string; venue: string }>> {
  return httpClient.get<{ date: string; venue: string }>(`api/noon-invite/${token}`);
}

/**
 * Check whether a noon conflict exists for the given date (YYYY-MM-DD).
 * @param date — the noon-service date itself; for a lodging booking that is
 * the morning AFTER the stay (D+1), not the booking date.
 */
async function checkNoonConflict(
  date: string,
  venue?: string,
): Promise<ApiResponse<{ hasConflict: boolean }>> {
  // `venue` (lasalle|serre) scopes the conflict to the lodging's physical side
  // — la salle/la table share the lasalle side, la serre is independent.
  // Omitting it keeps the global (any-side) check.
  return httpClient.get<{ hasConflict: boolean }>('api/noon-conflict', {
    params: { date, ...(venue ? { venue } : {}) },
  });
}

/**
 * Get Stripe payment status
 * @param id - Stripe payment ID
 */
async function getStripePaymentStatus(id: string): Promise<ApiResponse<any>> {
  return httpClient.get('api/stripe-status', {
    params: { stripe_id: id },
  });
}

/**
 * Fetch food order details by token
 * @param token - Order token
 */
async function fetchFoodOrder(token: string): Promise<ApiResponse<any>> {
  return httpClient.get('api/order', { params: { token } });
}

/**
 * Save menu choice for booking
 * @param bookingData - Complete booking data with menu choices
 */
async function saveMenuChoice(bookingData: any): Promise<ApiResponse<any>> {
  return httpClient.post('api/menu-selection', toWirePayload(bookingData));
}

/**
 * Validate VAT number using VIES service
 * @param vatNumber - VAT number to validate
 */
async function checkVatNumber(vatNumber: string): Promise<ApiResponse<any>> {
  return httpClient.post('api/vat-check', { vat: vatNumber });
}

/** Per-code outcome of a coupon validation call. */
export interface ValidatedCouponResult {
  code: string;
  status: string; // 'ok' | rejection codes (mirror of the backend union)
  type?: 'gift' | 'promo';
  amountAppliedCents?: number;
}

export interface CouponValidationResponse {
  results: ValidatedCouponResult[];
  discountCents: number;
  remainingDueCents: number;
}

/**
 * Validate coupon codes against the current booking total.
 * @param codes - Coupon codes entered by the customer
 * @param totalCents - Booking total, in cents, to apply the discount against
 */
async function validateCoupons(codes: string[], totalCents: number): Promise<ApiResponse<CouponValidationResponse>> {
  return httpClient.post<CouponValidationResponse>('api/coupons/validate', { codes, totalCents });
}

/**
 * Fire-and-forget analytics batch. Prefers a keepalive fetch (survives
 * tab-hide/unload); falls back to a no-retry, error-swallowed POST. Never
 * throws and never blocks the caller.
 *
 * Not navigator.sendBeacon: a beacon always sends credentials, and the
 * cross-origin API's CORS has no Allow-Credentials, so the browser rejects
 * the preflight and the event never leaves. `credentials: 'omit'` passes it.
 */
function trackAnalyticsEvents(events: AnalyticsEventPayload[]): void {
  if (!events.length) return;
  const url = 'api/analytics/events';
  const body = { events };
  try {
    if (typeof globalThis.fetch === 'function') {
      const base = (import.meta.env.VITE_API_ENDPOINT as string) || '/';
      const full = base.endsWith('/') ? base + url : `${base}/${url}`;
      globalThis.fetch(full, {
        method: 'POST',
        keepalive: true,
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).catch(() => { /* dropping analytics is acceptable */ });
      return;
    }
    void httpClient.post(url, body, undefined, { skipErrorHandling: true, retries: 0 });
  } catch {
    // never break the caller
  }
}

export {
  checkVatNumber,
  checkNoonConflict,
  getAvailability,
  getDrinks,
  getFoods,
  getServices,
  getOptions,
  getMenu,
  getTexts,
  getStripePaymentStatus,
  fetchFoodOrder,
  saveMenuChoice,
  startPayment,
  validateNoonInvite,
  validateCoupons,
  trackAnalyticsEvents,
};
